import { useState, useEffect } from "react";
import {
    Modal,
    Select,
    Typography,
    Spin,
    message,
    Empty,
    Tag,
    Card,
} from "antd";
import dayjs from "dayjs";
import BaseService from "../../../../service/base.service";
import { ExaminationFormValues } from "./MedicalConsultationModal";
import {
    FileSearchOutlined,
    CloseOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";

const { Text, Title } = Typography;

interface Props {
    open: boolean;
    onClose: () => void;
    patientId: string;
    formValues: ExaminationFormValues;
}

interface ExaminationSummary {
    id: number;
    examinationDate: string;
}

// 🔹 Field Labels
const fieldLabels: Record<keyof ExaminationFormValues, string> = {
    id: "Consultation ID",
    profession: "Profession",
    complaintVitae: "A. Vitae",
    complaintPharmacological: "A. Pharmacological",
    complaintFamily: "B. Family",
    complaintSocial: "A. Social",
    smokeUse: "Smoking Status",
    alcoholUse: "Alcohol Use",
    substanceUse: "Substance Use",
    lifestyle: "Lifestyle",
    diet: "Diet",
    physicalExamination: "Physical Examination",
    height: "Height (cm)",
    weight: "Weight (kg)",
    bmi: "BMI",
    diagnosis: "Diagnosis",
    recommendations: "Recommendations",
};

// 🔹 Section Grouping
const sections: { title: string; keys: (keyof ExaminationFormValues)[] }[] = [
    {
        title: "Lifestyle & Habits",
        keys: ["profession", "smokeUse", "alcoholUse", "substanceUse", "lifestyle", "diet"],
    },
    {
        title: "Complaints",
        keys: ["complaintVitae", "complaintPharmacological", "complaintFamily", "complaintSocial"],
    },
    {
        title: "Physical Measurements",
        keys: ["height", "weight", "bmi"],
    },
    {
        title: "Physical Examination",
        keys: ["physicalExamination"],
    },
    {
        title: "Diagnosis & Recommendations",
        keys: ["diagnosis", "recommendations"],
    },
];

// ✅ Comparison Helper
const areValuesEqual = (a: any, b: any): boolean => {
    if (!a && !b) return true;

    const parseAsDate = (val: any) => {
        if (dayjs.isDayjs(val)) return val;
        if (typeof val === "string") {
            const parsed = dayjs(val.trim(), "DD.MM.YYYY", true);
            return parsed.isValid() ? parsed : null;
        }
        return null;
    };

    const aDate = parseAsDate(a);
    const bDate = parseAsDate(b);
    if (aDate && bDate) return aDate.isSame(bDate, "day");

    if (Array.isArray(a) && Array.isArray(b)) {
        return JSON.stringify(a) === JSON.stringify(b);
    }

    return String(a ?? "").trim() === String(b ?? "").trim();
};

// ✅ Format Display Helper
const formatValue = (value: any): string => {
    if (!value) return "—";
    if (dayjs.isDayjs(value)) return value.format("DD.MM.YYYY");
    if (typeof value === "string") {
        const parsed = dayjs(value.trim(), "DD.MM.YYYY", true);
        return parsed.isValid() ? parsed.format("DD.MM.YYYY") : value.trim();
    }
    if (Array.isArray(value)) return value.join(", ");
    return String(value);
};

// 🔹 Main Modal Component
const MedicalConsultationComparisonModal = ({
    open,
    onClose,
    patientId,
    formValues,
}: Props) => {
    const [examList, setExamList] = useState<ExaminationSummary[]>([]);
    const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
    const [selectedExamDate, setSelectedExamDate] = useState<string | null>(null);
    const [selectedExam, setSelectedExam] = useState<ExaminationFormValues | null>(null);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingExam, setLoadingExam] = useState(false);
    const [changedCount, setChangedCount] = useState(0);

    // 🔹 Fetch list of previous consultations
    useEffect(() => {
        if (!open) return;
        setSelectedExamId(null);
        setSelectedExamDate(null);
        setSelectedExam(null);

        const fetchList = async () => {
            setLoadingList(true);
            try {
                const rp = await BaseService.get<ExaminationSummary[]>(
                    `/medical-consultations/${patientId}/compare-list`,
                    null
                );
                if (rp.Status && rp.Data) {
                    const sorted = rp.Data.sort(
                        (a: any, b: any) =>
                            new Date(b.examinationDate).getTime() -
                            new Date(a.examinationDate).getTime()
                    );
                    setExamList(sorted);
                } else {
                    message.error("Failed to load previous consultations list.");
                }
            } catch {
                message.error("Error loading previous consultations list.");
            } finally {
                setLoadingList(false);
            }
        };

        fetchList();
    }, [open, patientId]);

    // 🔹 Fetch selected exam for comparison
    const handleSelectCompare = async (id: number, option: any) => {
        setSelectedExamId(id);
        setSelectedExamDate(option?.label || null);
        setLoadingExam(true);
        try {
            const rp = await BaseService.get<ExaminationFormValues>(
                `/medical-consultations/details/${id}`,
                null
            );
            if (rp.Status) {
                const previousExam = rp.Data;
                setSelectedExam(previousExam);

                // count differences
                const changed = (Object.keys(fieldLabels) as (keyof ExaminationFormValues)[])
                    .filter((key) => key !== "id")
                    .filter((key) => !areValuesEqual(formValues[key], previousExam[key]));
                setChangedCount(changed.length);
            }
        } catch {
            message.error("Error fetching comparison consultation.");
        } finally {
            setLoadingExam(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width="80%"
            centered
            destroyOnClose
            closeIcon={<CloseOutlined style={{ fontSize: 18, color: "#555" }} />}
            title={
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: 6,
                        width: "100%",
                    }}
                >
                    <FileSearchOutlined style={{ color: "#1677ff", fontSize: 22 }} />
                    <Title level={4} style={{ margin: 0 }}>
                        Compare with Previous Consultation
                    </Title>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                        Review differences grouped by section.
                    </Text>
                </div>
            }
            bodyStyle={{
                padding: 24,
                maxHeight: "80vh",
                overflowY: "auto",
                background: "#fafafa",
            }}
        >
            {loadingList ? (
                <div style={{ textAlign: "center", padding: "60px 0" }}>
                    <Spin size="large" />
                </div>
            ) : examList.length === 0 ? (
                <Empty description="No previous consultations found" />
            ) : (
                <>
                    <Select
                        style={{ width: "100%", marginBottom: 20 }}
                        value={selectedExamId || undefined}
                        placeholder="Select a previous consultation date"
                        onChange={(value, option) => handleSelectCompare(value, option)}
                        options={examList.map((e) => {
                            const parsed = dayjs(e.examinationDate);
                            const formatted = parsed.isValid()
                                ? parsed.format("DD.MM.YYYY HH:mm")
                                : e.examinationDate;
                            return { label: formatted, value: e.id };
                        })}
                    />

                    {loadingExam ? (
                        <div style={{ textAlign: "center", padding: "50px 0" }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        selectedExam && (
                            <>
                                <div
                                    style={{
                                        background: "#fff",
                                        padding: "14px 18px",
                                        borderRadius: 8,
                                        marginBottom: 24,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <div>
                                        <Text strong>
                                            Current vs Previous ({selectedExamDate})
                                        </Text>
                                        <div style={{ color: "#888", fontSize: 13 }}>
                                            Reviewing {changedCount > 0 ? "changes" : "stable values"}
                                        </div>
                                    </div>
                                    <Tag
                                        color={changedCount > 0 ? "orange" : "green"}
                                        style={{ fontSize: 14, padding: "4px 10px" }}
                                    >
                                        {changedCount > 0
                                            ? `${changedCount} field${changedCount > 1 ? "s" : ""} changed`
                                            : "No Changes"}
                                    </Tag>
                                </div>

                                {sections.map((section) => {
                                    const sectionHasChange = section.keys.some((key) => {
                                        const current = formValues[key];
                                        const previous = selectedExam[key];
                                        return !areValuesEqual(current, previous);
                                    });

                                    return (
                                        <Card
                                            key={section.title}
                                            title={
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <Title level={5} style={{ margin: 0 }}>
                                                        {section.title}
                                                    </Title>
                                                    {sectionHasChange && (
                                                        <Tag
                                                            color="orange"
                                                            icon={<ExclamationCircleOutlined />}
                                                            style={{ marginLeft: 6 }}
                                                        >
                                                            Updated
                                                        </Tag>
                                                    )}
                                                </div>
                                            }
                                            style={{
                                                marginBottom: 24,
                                                background: sectionHasChange ? "#fffef8" : "#fff",
                                                boxShadow: sectionHasChange
                                                    ? "0 2px 6px rgba(250,173,20,0.15)"
                                                    : "0 1px 3px rgba(0,0,0,0.05)",
                                            }}
                                            headStyle={{ borderBottom: "1px solid #f0f0f0" }}
                                        >
                                            {/* ✅ one table per section */}
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr style={{ background: "#fafafa" }}>
                                                        <th style={{ textAlign: "left", padding: "8px 12px", width: "30%" }}>Field</th>
                                                        <th style={{ textAlign: "left", padding: "8px 12px", width: "35%" }}>Previous</th>
                                                        <th style={{ textAlign: "left", padding: "8px 12px", width: "35%" }}>Current</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {section.keys.map((key) => {
                                                        const current = formValues[key];
                                                        const previous = selectedExam[key];
                                                        const changed = !areValuesEqual(current, previous);
                                                        // ✅ Numeric delta detection
                                                        let numericDelta: string | null = null;
                                                        if (
                                                            changed &&
                                                            !isNaN(Number(current)) &&
                                                            !isNaN(Number(previous)) &&
                                                            current !== "" &&
                                                            previous !== ""
                                                        ) {
                                                            const diff = Number(current) - Number(previous);
                                                            if (diff !== 0) {
                                                                numericDelta =
                                                                    diff > 0 ? `+${diff.toFixed(1)}` : diff.toFixed(1);
                                                            }
                                                        }
                                                        return (
                                                            <tr
                                                                key={key}
                                                                style={{
                                                                    background: changed ? "#fff7e6" : "#fff",
                                                                    borderBottom: "1px solid #f0f0f0",
                                                                }}
                                                            >
                                                                {/* Field name */}
                                                                <td style={{ padding: "8px 12px", fontWeight: 500 }}>
                                                                    {fieldLabels[key]}
                                                                    {changed && (
                                                                        <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>
                                                                            Changed
                                                                        </Tag>
                                                                    )}
                                                                </td>

                                                                {/* Previous value */}
                                                                <td style={{ padding: "8px 12px", color: "#999" }}>
                                                                    {formatValue(previous)}
                                                                </td>
                                                                {/* Current value + delta */}
                                                                <td style={{ padding: "8px 12px" }}>
                                                                    {formatValue(current)}
                                                                    {numericDelta && (
                                                                        <Tag
                                                                            color={Number(numericDelta) > 0 ? "green" : "red"}
                                                                            style={{
                                                                                marginLeft: 8,
                                                                                fontSize: 11,
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {Number(numericDelta) > 0 ? "↑" : "↓"} {numericDelta}
                                                                        </Tag>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Card>
                                    );
                                })}
                            </>
                        )
                    )}
                </>
            )}
        </Modal>
    );
};

export default MedicalConsultationComparisonModal;
